import React, { useEffect, useRef, useState } from 'react';
import MessageActions from './MessageActions';
import EditMessage from './EditMessage';
import DeleteConfirmModal from './DeleteConfirmModal';
import MessageReactions from './MessageReactions';
import MentionInput from './MentionInput';
import MessageWithLinkPreview from './MessageWithLinkPreview';
import FilePreview from './FilePreview';
import { getHubSpotContext } from '../utils/hubspotContext';
import './Thread.css';

function Thread({ threadMessage, threadReplies, users, onClose, onSendReply, loading, onEditMessage, onDeleteMessage, currentUserId, onAddReaction, onRemoveReaction, onBookmark, onRemind, token, channelId }) {
  const messagesEndRef = useRef(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, message: null });
  const [replyText, setReplyText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [threadReplies]);

  const formatTime = (timestamp) => {
    const date = new Date(parseFloat(timestamp) * 1000);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleEdit = (message) => {
    setEditingMessageId(message.ts);
  };

  const handleSaveEdit = (message, newText) => {
    onEditMessage(message, newText);
    setEditingMessageId(null);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
  };

  const handleDelete = (message) => {
    setDeleteModal({ isOpen: true, message });
  };

  const confirmDelete = () => {
    if (deleteModal.message) {
      onDeleteMessage(deleteModal.message);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, message: null });
  };

  const handleReply = (message) => {
    // Thread replies don't have nested threads
    console.log('Reply to thread message:', message);
  };

  const handleSendThreadReply = async (text, mentions) => {
    // Only proceed if there's text or files to send
    if (!text.trim() && attachedFiles.length === 0) return;

    setIsUploading(true);

    try {
      // If there are files to upload
      if (attachedFiles.length > 0) {
        const uploadedFiles = [];
        for (const file of attachedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('channels', channelId);
          formData.append('thread_ts', threadMessage.ts);
          if (text.trim()) {
            formData.append('initial_comment', text);
          }

          // Add HubSpot sync data if enabled
          const hubspotSyncEnabled = localStorage.getItem('hubspot_sync_enabled') === 'true';
          const hubspotContext = getHubSpotContext();
          if (hubspotSyncEnabled && hubspotContext) {
            formData.append('hubspotSync', JSON.stringify({
              enabled: true,
              portalId: hubspotContext.portalId,
              userId: hubspotContext.userId,
              contactId: hubspotContext.contactId,
              channelName: channelId
            }));
          }

          // Use the token prop instead of localStorage
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
          const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
            method: 'POST',
            headers: {
              'x-slack-token': token
            },
            body: formData,
            credentials: 'include'
          });

          if (!response.ok) {
            throw new Error('Failed to upload file');
          }

          const result = await response.json();
          uploadedFiles.push(result.file);
        }

        // After uploading files, trigger message refresh
        if (uploadedFiles.length > 0) {
          onSendReply('', uploadedFiles);
        }
      } else if (text.trim()) {
        // Only text, no files
        onSendReply(text);
      }

      // Clear everything after successful send
      setReplyText('');
      setAttachedFiles([]);
    } catch (error) {
      console.error('Error sending thread reply with files:', error);
      alert('Failed to send reply with files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!threadMessage) return null;

  const parentUser = users[threadMessage.user] || { real_name: 'Unknown User' };

  return (
    <div className="thread-panel">
      <div className="thread-header">
        <h3>Thread</h3>
        <button className="thread-close" onClick={onClose}>×</button>
      </div>
      
      <div className="thread-messages">
        <div className="thread-parent-message">
          <div className={`message ${threadMessage.is_starred ? 'message-bookmarked' : ''}`}>
            <div className="message-avatar">
              {parentUser.profile?.image_48 ? (
                <img src={parentUser.profile.image_48} alt={parentUser.real_name} />
              ) : (
                <div className="avatar-placeholder">
                  {parentUser.real_name?.charAt(0) || '?'}
                </div>
              )}
            </div>
            <div className="message-content">
              <div className="message-header">
                <span className="message-author">{parentUser.real_name || parentUser.name}</span>
                <span className="message-time">{formatTime(threadMessage.ts)}</span>
                {threadMessage.edited && <span className="message-edited">(edited)</span>}
              </div>
              {editingMessageId === threadMessage.ts ? (
                <EditMessage
                  message={threadMessage}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <MessageWithLinkPreview message={threadMessage} users={users} />
              )}
              {threadMessage.files && threadMessage.files.length > 0 && (
                <div className="message-files">
                  {threadMessage.files.map((file, fileIndex) => (
                    <FilePreview key={file.id || fileIndex} file={file} />
                  ))}
                </div>
              )}
              {threadMessage.reactions && threadMessage.reactions.length > 0 && (
                <MessageReactions
                  reactions={threadMessage.reactions}
                  messageId={threadMessage.ts}
                  onAddReaction={(messageId, emoji) => onAddReaction(threadMessage, emoji)}
                  onRemoveReaction={(messageId, emoji) => onRemoveReaction(threadMessage, emoji)}
                  currentUserId={currentUserId}
                  users={users}
                />
              )}
            </div>
            {threadMessage.is_starred && (
              <div className="message-bookmark-indicator" title="Saved for later">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 2h10v12l-5-3-5 3V2z" fill="#1d1c1d"/>
                </svg>
                <span>Saved for later</span>
                {threadMessage.reminder_time && (() => {
                  const now = Math.floor(Date.now() / 1000);
                  const timeLeft = threadMessage.reminder_time - now;
                  if (timeLeft > 0) {
                    const minutes = Math.floor(timeLeft / 60);
                    const hours = Math.floor(minutes / 60);
                    const days = Math.floor(hours / 24);

                    let timeText;
                    if (days > 0) {
                      timeText = `Due in ${days} day${days > 1 ? 's' : ''}`;
                    } else if (hours > 0) {
                      timeText = `Due in ${hours} hour${hours > 1 ? 's' : ''}`;
                    } else if (minutes > 0) {
                      timeText = `Due in ${minutes} min${minutes > 1 ? 's' : ''}`;
                    } else {
                      timeText = 'Due soon';
                    }

                    return (
                      <>
                        <span className="reminder-separator">•</span>
                        <span className="reminder-due-time">{timeText}</span>
                      </>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <MessageActions
              message={threadMessage}
              currentUserId={currentUserId}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onReply={handleReply}
              onAddReaction={onAddReaction}
              onBookmark={onBookmark}
              onRemind={onRemind}
            />
          </div>
        </div>

        {threadReplies.length > 0 && (
          <div className="thread-separator">
            <span>{threadReplies.length} {threadReplies.length === 1 ? 'reply' : 'replies'}</span>
          </div>
        )}
        
        <div className="thread-replies">
          {threadReplies.map((reply, index) => {
            const replyUser = users[reply.user] || { real_name: 'Unknown User' };
            return (
              <div key={reply.ts || index} className={`message thread-reply ${reply.is_starred ? 'message-bookmarked' : ''}`}>
                <div className="message-avatar">
                  {replyUser.profile?.image_48 ? (
                    <img src={replyUser.profile.image_48} alt={replyUser.real_name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {replyUser.real_name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-author">{replyUser.real_name || replyUser.name}</span>
                    <span className="message-time">{formatTime(reply.ts)}</span>
                    {reply.edited && <span className="message-edited">(edited)</span>}
                  </div>
                  {editingMessageId === reply.ts ? (
                    <EditMessage
                      message={reply}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                    />
                  ) : (
                    <MessageWithLinkPreview message={reply} users={users} />
                  )}
                  {reply.files && reply.files.length > 0 && (
                    <div className="message-files">
                      {reply.files.map((file, fileIndex) => (
                        <FilePreview key={file.id || fileIndex} file={file} />
                      ))}
                    </div>
                  )}
                  {reply.reactions && reply.reactions.length > 0 && (
                    <MessageReactions
                      reactions={reply.reactions}
                      messageId={reply.ts}
                      onAddReaction={(messageId, emoji) => onAddReaction(reply, emoji)}
                      onRemoveReaction={(messageId, emoji) => onRemoveReaction(reply, emoji)}
                      currentUserId={currentUserId}
                      users={users}
                    />
                  )}
                </div>
                {reply.is_starred && (
                  <div className="message-bookmark-indicator" title="Saved for later">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M3 2h10v12l-5-3-5 3V2z" fill="#1d1c1d"/>
                    </svg>
                    <span>Saved for later</span>
                    {reply.reminder_time && (() => {
                      const now = Math.floor(Date.now() / 1000);
                      const timeLeft = reply.reminder_time - now;
                      if (timeLeft > 0) {
                        const minutes = Math.floor(timeLeft / 60);
                        const hours = Math.floor(minutes / 60);
                        const days = Math.floor(hours / 24);

                        let timeText;
                        if (days > 0) {
                          timeText = `Due in ${days} day${days > 1 ? 's' : ''}`;
                        } else if (hours > 0) {
                          timeText = `Due in ${hours} hour${hours > 1 ? 's' : ''}`;
                        } else if (minutes > 0) {
                          timeText = `Due in ${minutes} min${minutes > 1 ? 's' : ''}`;
                        } else {
                          timeText = 'Due soon';
                        }

                        return (
                          <>
                            <span className="reminder-separator">•</span>
                            <span className="reminder-due-time">{timeText}</span>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
                <MessageActions
                  message={reply}
                  currentUserId={currentUserId}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onReply={handleReply}
                  onAddReaction={onAddReaction}
                  onBookmark={onBookmark}
                  onRemind={onRemind}
                />
              </div>
            );
          })}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* File Preview Section */}
      {attachedFiles.length > 0 && (
        <div className="attached-files-preview" style={{
          padding: '8px 16px',
          borderTop: '1px solid #e1e1e1',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          backgroundColor: '#f8f8f8'
        }}>
          {attachedFiles.map((file, index) => (
            <div key={index} className="attached-file-item" style={{
              display: 'flex',
              alignItems: 'center',
              padding: '4px 8px',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '13px',
              gap: '4px'
            }}>
              <span>📎</span>
              <span>{file.name}</span>
              <button
                onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: '16px'
                }}
                aria-label="Remove file"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="thread-input">
        <MentionInput
          value={replyText}
          onChange={setReplyText}
          onSend={handleSendThreadReply}
          users={users}
          channelId={channelId}
          token={token}
          onFileSelect={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length === 0) return;

            // Validate file sizes
            for (const file of files) {
              if (file.size > 100 * 1024 * 1024) {
                alert(`File ${file.name} is too large. Maximum size is 100MB.`);
                continue;
              }
              // Add file to local state (not uploading yet)
              setAttachedFiles(prev => [...prev, file]);
            }

            // Reset file input
            if (e.target) {
              e.target.value = '';
            }
          }}
          isUploading={isUploading}
          hasAttachments={attachedFiles.length > 0}
          placeholder={isUploading ? "Uploading..." : attachedFiles.length > 0 ? `Type a message (${attachedFiles.length} file${attachedFiles.length > 1 ? 's' : ''} attached). Ctrl+Enter to send` : "Reply... (Ctrl+Enter to send)"}
          enableFormatting={true}
        />
      </div>
      
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={deleteModal.message}
        user={deleteModal.message ? users[deleteModal.message.user] : null}
      />
    </div>
  );
}

export default Thread;